import React, { useEffect, useState } from 'react';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { C, ACCENT } from '../../theme';
import { useToast } from '../../components/Toast';
import { Plus, Trash2, Edit2 } from 'lucide-react';

const TABS = ['Sites', 'Overlap Matrix'];

const BLANK_SITE = {
  name: '', domain: '',
  monthly_real_users_total: 0,
  monthly_real_users_desktop: '',
  monthly_real_users_mobile: '',
  monthly_real_users_app: '',
  active: true,
};

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(80,120,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
      <div style={{...C.modal, width:520}} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a2a3a' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#8aa0b8', fontSize:22, cursor:'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function numOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}

export default function SitesPage() {
  const { canEditCatalogue } = useAuth();
  const { showToast } = useToast();
  const [tab, setTab] = useState('Sites');
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | site object
  const [form, setForm] = useState(BLANK_SITE);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Overlap matrix state: { "a|b": coefficient }
  const [overlapMatrix, setOverlapMatrix] = useState({});
  const [matrixSaving, setMatrixSaving] = useState(false);

  function load() {
    setLoading(true);
    Promise.all([api.getSites(), api.getSiteOverlaps()])
      .then(([s, o]) => {
        setSites(s);
        const m = {};
        o.forEach(ov => { m[`${ov.site_a_id}|${ov.site_b_id}`] = ov.overlap_coefficient; });
        setOverlapMatrix(m);
      })
      .catch(() => showToast('Failed to load sites', 'danger'))
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  function openNew() { setForm({...BLANK_SITE}); setModal('new'); }
  function openEdit(s) {
    setForm({
      name: s.name, domain: s.domain || '',
      monthly_real_users_total: s.monthly_real_users_total || 0,
      monthly_real_users_desktop: s.monthly_real_users_desktop ?? '',
      monthly_real_users_mobile: s.monthly_real_users_mobile ?? '',
      monthly_real_users_app: s.monthly_real_users_app ?? '',
      active: s.active,
    });
    setModal(s);
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      domain: form.domain.trim() || null,
      monthly_real_users_total: parseInt(form.monthly_real_users_total, 10) || 0,
      monthly_real_users_desktop: numOrNull(form.monthly_real_users_desktop),
      monthly_real_users_mobile: numOrNull(form.monthly_real_users_mobile),
      monthly_real_users_app: numOrNull(form.monthly_real_users_app),
      active: form.active,
    };
    try {
      if (modal === 'new') await api.createSite(payload);
      else await api.updateSite(modal.id, payload);
      showToast(modal === 'new' ? 'Site created' : 'Site saved', 'success');
      await load(); setModal(null);
    } catch(e) { showToast(e.message || 'Save failed', 'danger'); }
    finally { setSaving(false); }
  }

  async function handleDelete(site) {
    try {
      await api.deleteSite(site.id);
      showToast(`Deleted ${site.name}`, 'success');
      setDeleteConfirm(null);
      await load();
    } catch(e) { showToast(e.message || 'Delete failed — platforms still reference this site', 'danger'); setDeleteConfirm(null); }
  }

  async function saveMatrix() {
    setMatrixSaving(true);
    const items = Object.entries(overlapMatrix).map(([key, coeff]) => {
      const [a, b] = key.split('|').map(Number);
      return { site_a_id: Math.min(a,b), site_b_id: Math.max(a,b), overlap_coefficient: parseFloat(coeff) || 0.40 };
    });
    try {
      await api.updateSiteOverlaps(items);
      showToast('Overlap matrix saved', 'success');
    } catch(e) { showToast('Save failed', 'danger'); }
    finally { setMatrixSaving(false); }
  }

  function setOvKey(aId, bId, val) {
    const a = Math.min(aId,bId), b = Math.max(aId,bId);
    setOverlapMatrix(m => ({ ...m, [`${a}|${b}`]: val }));
  }
  function getOv(aId, bId) {
    const a = Math.min(aId,bId), b = Math.max(aId,bId);
    return overlapMatrix[`${a}|${b}`] ?? 0.40;
  }

  const fNum = (key, label, helpText) => (
    <label style={{ display:'block', marginBottom:14 }}>
      <span style={C.label}>{label}</span>
      <input
        type="text" inputMode="numeric"
        defaultValue={form[key] === '' || form[key] === null ? '' : form[key]}
        key={`k-${key}-${modal === 'new' ? 'new' : modal?.id}`}
        onBlur={e => {
          const v = e.target.value === '' ? '' : parseInt(e.target.value, 10);
          setForm(f => ({ ...f, [key]: isNaN(v) ? '' : v }));
        }}
        style={C.input}
      />
      {helpText && <span style={{ fontSize:11, color:'#8aa0b8', marginTop:4, display:'block' }}>{helpText}</span>}
    </label>
  );

  return (
    <div style={C.page}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <h1 style={C.title}>Sites</h1>
        {canEditCatalogue && tab === 'Sites' && (
          <button style={{...C.btnPrimary, display:'inline-flex', alignItems:'center', gap:6}} onClick={openNew}>
            <Plus size={14}/>Add Site
          </button>
        )}
        {canEditCatalogue && tab === 'Overlap Matrix' && (
          <button style={{...C.btnPrimary, display:'inline-flex', alignItems:'center', gap:6}} onClick={saveMatrix} disabled={matrixSaving}>
            {matrixSaving ? 'Saving…' : 'Save Matrix'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'1px solid #dce8f0' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding:'8px 18px', border:'none', background:'none', cursor:'pointer',
            fontSize:13, fontWeight:600,
            color: tab===t ? ACCENT : '#6a8aaa',
            borderBottom: tab===t ? `2px solid ${ACCENT}` : '2px solid transparent',
            marginBottom:-1,
          }}>{t}</button>
        ))}
      </div>

      {/* ── Sites tab ── */}
      {tab === 'Sites' && (
        loading ? <div style={C.empty}>Loading…</div> :
        sites.length === 0 ? <div style={C.empty}>No sites yet. Add your first site.</div> : (
          <table style={C.table}>
            <thead>
              <tr>
                <th style={C.th}>Name</th>
                <th style={C.th}>Domain</th>
                <th style={{...C.th, textAlign:'right'}}>Total Users/mo</th>
                <th style={{...C.th, textAlign:'right'}}>Desktop</th>
                <th style={{...C.th, textAlign:'right'}}>Mobile</th>
                <th style={{...C.th, textAlign:'right'}}>App</th>
                <th style={C.th}>Status</th>
                {canEditCatalogue && <th style={C.th}></th>}
              </tr>
            </thead>
            <tbody>
              {sites.map(s => (
                <tr key={s.id} style={C.tr}
                  onMouseEnter={e=>e.currentTarget.style.background='rgba(0,144,208,0.04)'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{...C.td, fontWeight:700}}>{s.name}</td>
                  <td style={{...C.td, color:'#6a8aaa'}}>{s.domain || '—'}</td>
                  <td style={{...C.td, textAlign:'right'}}>{(s.monthly_real_users_total||0).toLocaleString()}</td>
                  <td style={{...C.td, textAlign:'right', color:'#6a8aaa'}}>{s.monthly_real_users_desktop != null ? s.monthly_real_users_desktop.toLocaleString() : '—'}</td>
                  <td style={{...C.td, textAlign:'right', color:'#6a8aaa'}}>{s.monthly_real_users_mobile != null ? s.monthly_real_users_mobile.toLocaleString() : '—'}</td>
                  <td style={{...C.td, textAlign:'right', color:'#6a8aaa'}}>{s.monthly_real_users_app != null ? s.monthly_real_users_app.toLocaleString() : '—'}</td>
                  <td style={C.td}>
                    <span style={C.statusRow()}>
                      <span style={C.statusDot(s.active ? 'success' : 'neutral')}/>
                      {s.active ? 'active' : 'inactive'}
                    </span>
                  </td>
                  {canEditCatalogue && (
                    <td style={{...C.td, textAlign:'right'}}>
                      <div style={{ display:'inline-flex', gap:8 }}>
                        <button onClick={()=>openEdit(s)} style={{ background:'none', border:'none', cursor:'pointer', color:'#8aa0b8', padding:4 }} title="Edit"><Edit2 size={14}/></button>
                        <button onClick={()=>setDeleteConfirm(s)} style={{ background:'none', border:'none', cursor:'pointer', color:'#a02020', padding:4 }} title="Delete"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {/* ── Overlap Matrix tab ── */}
      {tab === 'Overlap Matrix' && (
        loading ? <div style={C.empty}>Loading…</div> :
        sites.length < 2 ? <div style={C.empty}>Add at least 2 sites to configure overlap coefficients.</div> : (
          <div>
            <p style={{ fontSize:13, color:'#6a8aaa', marginBottom:16 }}>
              Overlap coefficient (0.00–1.00) between each pair of sites. Higher values mean more shared audience, producing lower combined reach. Default: 0.40.
            </p>
            <div style={{ overflowX:'auto' }}>
              <table style={{ ...C.table, width:'auto' }}>
                <thead>
                  <tr>
                    <th style={{...C.th, background:'#e8f0f8', minWidth:120}}></th>
                    {sites.map(s => <th key={s.id} style={{...C.th, background:'#e8f0f8', minWidth:100, textAlign:'center'}}>{s.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {sites.map((rowSite, ri) => (
                    <tr key={rowSite.id} style={C.tr}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(0,144,208,0.04)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{...C.td, fontWeight:700, background:'#f0f6fb'}}>{rowSite.name}</td>
                      {sites.map((colSite, ci) => {
                        if (rowSite.id === colSite.id) {
                          return <td key={colSite.id} style={{...C.td, textAlign:'center', color:'#c0ccd8', background:'#f8fafc'}}>—</td>;
                        }
                        // Only edit upper triangle; lower mirrors
                        if (ri > ci) {
                          return (
                            <td key={colSite.id} style={{...C.td, textAlign:'center', color:'#8aa0b8', fontSize:12}}>
                              {parseFloat(getOv(rowSite.id, colSite.id)).toFixed(2)}
                            </td>
                          );
                        }
                        return (
                          <td key={colSite.id} style={{...C.td, textAlign:'center'}}>
                            <input
                              type="text" inputMode="decimal"
                              defaultValue={parseFloat(getOv(rowSite.id, colSite.id)).toFixed(2)}
                              key={`ov-${rowSite.id}-${colSite.id}`}
                              onBlur={e => {
                                const v = parseFloat(e.target.value);
                                setOvKey(rowSite.id, colSite.id, isNaN(v) ? 0.40 : Math.max(0, Math.min(1, v)));
                              }}
                              style={{ ...C.input, width:64, textAlign:'center', padding:'4px 8px' }}
                              disabled={!canEditCatalogue}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* ── Edit / New Site Modal ── */}
      {modal && (
        <Modal title={modal === 'new' ? 'Add Site' : `Edit ${modal.name}`} onClose={() => setModal(null)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' }}>
            <div style={{ gridColumn:'1/-1' }}>
              <label style={{ display:'block', marginBottom:14 }}>
                <span style={C.label}>Name</span>
                <input type="text" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={C.input} placeholder="e.g. YourSite"/>
              </label>
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <label style={{ display:'block', marginBottom:14 }}>
                <span style={C.label}>Domain</span>
                <input type="text" value={form.domain} onChange={e=>setForm(f=>({...f,domain:e.target.value}))} style={C.input} placeholder="e.g. yoursite.com"/>
              </label>
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              {fNum('monthly_real_users_total', 'Monthly Users — Total')}
            </div>
            {fNum('monthly_real_users_desktop', 'Desktop Users')}
            {fNum('monthly_real_users_mobile', 'Mobile Users')}
            {fNum('monthly_real_users_app', 'App Users')}
          </div>
          <p style={{ fontSize:11, color:'#8aa0b8', margin:'0 0 16px' }}>
            If total differs from the sum of device counts, cross-device overlap is derived automatically.
          </p>
          <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20, cursor:'pointer' }}>
            <input type="checkbox" checked={form.active} onChange={e=>setForm(f=>({...f,active:e.target.checked}))}/>
            <span style={{ fontSize:13, color:'#1a2a3a' }}>Active</span>
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setModal(null)}>Cancel</button>
            <button style={C.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </Modal>
      )}

      {/* ── Delete confirm ── */}
      {deleteConfirm && (
        <Modal title="Delete Site" onClose={()=>setDeleteConfirm(null)}>
          <p style={{ fontSize:14, color:'#1a2a3a', marginBottom:20 }}>
            Delete <strong>{deleteConfirm.name}</strong>? This will fail if any platforms still reference this site — unlink them first.
          </p>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setDeleteConfirm(null)}>Cancel</button>
            <button style={C.btnDanger} onClick={()=>handleDelete(deleteConfirm)}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
